import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "../../api/axios";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../context/StoreContext"; // ✅ Import StoreContext
import './styles/AdminAddProduct.css';
import { regenerateSitemap } from "../../utility/setSitemap";
import { getImageUrl } from "../../utility/imageHelper";

const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // ✅ 20MB Image Limit

const AdminProductForm = ({mode}) => {
    const navigate = useNavigate();
    const { fetchProducts } = useStore();
    const fileInputRef = useRef(null);

    const { productId } = useParams();
    const isEdit = mode === "edit";

    const [deletedImageUrls, setDeletedImageUrls] = useState([]);

    
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [newTag, setNewTag] = useState("");
    const [product, setProduct] = useState({
        name: "",
        description: "",
        price: "",
        stock: 1,
        category_id: "",
        sheath_included: 1,   // default = included
        featured: 0,
    });





    const [categories, setCategories] = useState([]);
    const [images, setImages] = useState([]);
    const [draggedImageIndex, setDraggedImageIndex] = useState(null);
    const [csrfToken, setCsrfToken] = useState("");

    useEffect(() => {
        const token = document.cookie
            .split("; ")
            .find(row => row.startsWith("csrfToken="))
            ?.split("=")[1];
        if (token) setCsrfToken(token);
    }, []);


    const fetchProduct = useCallback(async () => {
        if (!isEdit || !productId) return;

        try {
            const res = await axios.get(`/api/products/${productId}`, {
            withCredentials: true,
            });

            const data = res.data.data;

            setProduct({
            name: data.name,
            description: data.description,
            price: data.price,
            stock: data.stock,
            category_id: data.category_id,
            sheath_included: data.sheath_included ?? 1,
            featured: data.featured ?? 0,
            });

            setImages(
            data.images
                .sort((a, b) => a.order - b.order)
                .filter(img => !deletedImageUrls.includes(img.image_url))
                .map(img => ({
                existing: true,
                image_url: img.image_url,
                order: img.order,
                preview: getImageUrl(img.image_url),
                }))
            );

            const tagsRes = await axios.get(`/api/products/${productId}/tags`);
            setSelectedTags(tagsRes.data.data.map(t => t.id));
        } catch (err) {
            console.error("Failed to load product", err);
        }
        }, [isEdit, productId, deletedImageUrls]);



    useEffect(() => {
    if (!isEdit || !productId) return;
    fetchProduct();
    }, [isEdit, productId, fetchProduct]);


    useEffect(() => {
    if (!isEdit) {
        setProduct({
        name: "",
        description: "",
        price: "",
        stock: 1,
        category_id: "",
        sheath_included: 1,
        featured: 0,
        });
        setImages([]);
        setSelectedTags([]);
        setDeletedImageUrls([]);
    }
    }, [isEdit]);


    useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await axios.get("/api/products/tags", { withCredentials: true });
                setTags(response.data.data);
            } catch (error) {
                console.error("🔥 Error fetching tags:", error.message);
            }
        };
        fetchTags();
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get("/api/products/categories", { withCredentials: true });
                setCategories(response.data.data);
            } catch (error) {
                console.error("🔥 Error fetching categories:", error.message);
            }
        };
        fetchCategories();
    }, []);

    const handleChange = (e) => {
        setProduct({ ...product, [e.target.name]: e.target.value });
    };

    const handleImageChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    // 🔥 EDIT MODE → upload immediately
    if (isEdit) {
        const formData = new FormData();
        selectedFiles.forEach(file => formData.append("images", file));

        await axios.post(
        `/api/admin/products/${productId}/images`,
        formData,
        {
            withCredentials: true,
            headers: {
            "x-csrf-token": csrfToken,
            "Content-Type": "multipart/form-data",
            },
        }
        );

        // 🔁 Re-fetch product images so they become "existing"
        await fetchProduct(); // same function you already have
        e.target.value = "";
        return;
    }

    // 🆕 ADD MODE → keep local
    const validImages = selectedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
    }));

    setImages(prev => [...prev, ...validImages]);
    e.target.value = "";
    };

    
    const moveImageUp = (index) => {
        if (index === 0) return;
        const updatedImages = [...images];
        [updatedImages[index - 1], updatedImages[index]] = [updatedImages[index], updatedImages[index - 1]];
        setImages(updatedImages);
    };
    
    const moveImageDown = (index) => {
        if (index === images.length - 1) return;
        const updatedImages = [...images];
        [updatedImages[index], updatedImages[index + 1]] = [updatedImages[index + 1], updatedImages[index]];
        setImages(updatedImages);
    };
    
    const makeMainImage = (index) => {
        if (index === 0) return; // Already the main image
        const updatedImages = [...images];
        const [selectedImage] = updatedImages.splice(index, 1);
        updatedImages.unshift(selectedImage); // Move to first position
        setImages(updatedImages);
    };
      

    const handleTagToggle = (tagId) => {
        setSelectedTags((prevTags) =>
            prevTags.includes(tagId)
                ? prevTags.filter(id => id !== tagId)
                : [...prevTags, tagId]
        );
    };

    const handleNewTagKeyPress = async (e) => {
      if (e.key === "Enter" && newTag.trim() !== "") {
          e.preventDefault();
  
          try {
              const tagResponse = await axios.post("/api/admin/tags", { name: newTag }, {
                  withCredentials: true,
                  headers: { "x-csrf-token": csrfToken },
              });
  
              if (tagResponse.data.success) {
                  const newTagData = tagResponse.data.data;
                  setTags((prev) => [...prev, newTagData]);
  
                  setSelectedTags((prev) => [...prev, newTagData.id]);
                  setNewTag("");
              }
          } catch (error) {
              console.error("🔥 Error adding new tag:", error.message);
          }
      }
   };

   const handleAddTag = async () => {
    if (newTag.trim() !== "") {
      await handleNewTagKeyPress({ key: "Enter", preventDefault: () => {} });
    }
  };
  
  
    

    const handleSubmit = async (e) => {
    e.preventDefault();

    try {
        let productIdToUse = productId;

        // 1️⃣ Create or update product
        if (!isEdit) {
        const res = await axios.post(
            "/api/admin/products",
            product,
            { withCredentials: true, headers: { "x-csrf-token": csrfToken } }
        );
        productIdToUse = res.data.data.id;
        } else {
        await axios.put(
            `/api/admin/products/${productId}`,
            {
            ...product,
            imageOrders: images
                .filter(img => img.existing && img.image_url)
                .map((img, index) => ({
                imageUrl: img.image_url,
                order: index + 1,
                })),
            },
            {
            withCredentials: true,
            headers: { "x-csrf-token": csrfToken },
            }
        );
        }

        if (isEdit && deletedImageUrls.length > 0) {
        for (const imageUrl of deletedImageUrls) {
            await axios.delete(
            `/api/admin/products/${productIdToUse}/images`,
            {
                data: { imageUrl },
                withCredentials: true,
                headers: { "x-csrf-token": csrfToken },
            }
            );
        }
        }


        // 3️⃣ Upload new images
        const newImages = images.filter(img => img.file);
        if (newImages.length) {
        const formData = new FormData();
        newImages.forEach(img => formData.append("images", img.file));

        await axios.post(
            `/api/admin/products/${productIdToUse}/images`,
            formData,
            { withCredentials: true, headers: { "x-csrf-token": csrfToken } }
        );
        }



        // 4 Sync tags
        await axios.post(
        `/api/admin/products/${productIdToUse}/tags`,
        { tagIds: selectedTags },
        { withCredentials: true, headers: { "x-csrf-token": csrfToken } }
        );

        // 6️⃣ Exit
        setDeletedImageUrls([]);
        navigate("/admin/products", { replace: true });
        fetchProducts();
        regenerateSitemap();

    } catch (err) {
        console.error("Submit failed", err);
    }
    };




return (
    <div className="admin-messages-container">
        <form onSubmit={handleSubmit}>
  <div className="product-details-wrapper admin-product-edit">
    <h2>{isEdit && "Edit Product" || "Add Product"}</h2>
    
    <div className="product-details-container">

      {/* ✅ LEFT COLUMN — matches ProductDetails */}
        {/* LEFT COLUMN — fully mimics ProductDetails */}
        {/* LEFT COLUMN — mimics ProductDetails but with admin controls */}
        <div className="product-left">
        <div className="product-images">

            {/* MAIN IMAGE AREA */}
            <div className="image-zoom-wrapper">

            {/* Placeholder when empty */}
            {images.length === 0 && (
                <div
                className="main-image placeholder-upload"
                onClick={() => fileInputRef.current?.click()}
                role="button"
                >
                <div className="placeholder-inner">
                    <i className="fa-solid fa-upload upload-icon"></i>
                    <p>Click to upload an image</p>
                </div>
                </div>
            )}

            {/* Main image preview */}
            {images.length > 0 && (
                <figure className="iiz main-image">
                <div>
                    <img
                    src={images[0].preview}
                    alt="Main Preview"
                    className="iiz__img"
                    draggable={false}
                    />
                </div>
                </figure>
            )}
            </div>

            {images.length > 1 && <span>Drag and drop or use buttons to reorder images</span>}
            {/* THUMBNAILS */}
            {images.length > 0 && (
            <div className="thumbnail-container">
                
                {images.map((img, index) => (
                <div
                    key={index}
                    className="admin-thumb-wrapper"
                    draggable
                    onDragStart={(e) => {
                    setDraggedImageIndex(index);
                    e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                    e.preventDefault();
                    if (draggedImageIndex === null || draggedImageIndex === index) return;

                    const reordered = [...images];
                    const [moved] = reordered.splice(draggedImageIndex, 1);
                    reordered.splice(index, 0, moved);

                    setImages(reordered);
                    setDraggedImageIndex(index);
                    }}
                    onDrop={() => setDraggedImageIndex(null)}
                >
                    {/* Thumbnail image */}
                    <img
                    src={img.preview}
                    alt={`Preview ${index}`}
                    className={`admin-thumb-img ${index === 0 ? "active" : ""}`}
                    />

                    {/* ADMIN CONTROLS */}
                    <div className="image-controls">

                        {/* Set as main */}
                        <button
                            type="button"
                            onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            makeMainImage(index);
                            }}
                        >
                            ⭐
                        </button>

                        {/* Reorder up/down */}
                        <div className="image-reorder-buttons">
                            <button
                            type="button"
                            disabled={index === 0}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                moveImageUp(index);
                            }}
                            >
                            ⬆️
                            </button>

                            <button
                            type="button"
                            disabled={index === images.length - 1}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                moveImageDown(index);
                            }}
                            >
                            ⬇️
                            </button>
                        </div>

                        {/* Delete */}
                        <button
                        type="button"
                        className="delete-thumb-btn"
                        onClick={() => {
                            const img = images[index];

                            if (img.existing) {
                            setDeletedImageUrls(prev => [...prev, img.image_url]);
                            }

                            setImages(prev => prev.filter((_, i) => i !== index));
                        }}
                        >
                        ❌
                        </button>

                        </div>

                </div>
                ))}

                {/* ADD IMAGE THUMBNAIL */}
                <div
                className="thumbnail upload-thumb"
                onClick={() => fileInputRef.current?.click()}
                role="button"
                >
                <i className="fa-solid fa-plus upload-thumb-icon"></i>
                </div>

            </div>
            )}

            {/* One hidden file input — always mounted */}
            <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageChange}
            className="hidden-upload-input"
            />

        </div>
        </div>



      {/* ✅ RIGHT COLUMN — matches ProductDetails */}
      <div className="product-info">
        <div className="product-info-content">

          {/* Category */}
          <label className="product-category-link">
            <select
              name="category_id"
              value={product.category_id}
              onChange={handleChange}
              className="input-field"
            >
              <option value="">Select category…</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </label>

          {/* Product Name */}
          <h1>
            <input
              type="text"
              name="name"
              className="input-field"
              placeholder="Product Name"
              required
              value={product.name}
              onChange={handleChange}
            />
          </h1>

            {/* Description */}
          <p className="product-description">
            <textarea
              name="description"
              className="input-field"
              placeholder="Enter product description"
              value={product.description}
              onChange={handleChange}
            />
          </p>

          {/* Maker Tags (editable version) */}
          <div className="maker-tag-line admin-maker-tags">
            <strong>Tags/Specifications:</strong>

              <div className="specs-wrapper">
                <label className="form-label checkbox-label">
                  <input
                    type="checkbox"
                    checked={product.sheath_included === 1}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        sheath_included: e.target.checked ? 1 : 0,
                      })
                    }
                  />
                  Sheath Included?
                </label>
              </div>

            <div className="tags-list">
              {tags.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  className={`tag-button ${
                    selectedTags.includes(tag.id) ? "selected" : ""
                  }`}
                  onClick={() => handleTagToggle(tag.id)}
                >
                  {tag.name} {selectedTags.includes(tag.id) && "❌" || "➕"}
                </button>
              ))}
            </div>

            {/* Add new tag */}
            <div className="new-tag-input">
              <input
                type="text"
                placeholder="Type a new tag and press Enter..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={handleNewTagKeyPress}
                className="input-field"
              />
              <button type="button" className="add-tag-btn" onClick={handleAddTag}>
                ➕ Add New Tag
              </button>
            </div>
          </div>
          <hr style={{margin: "1rem 0"}}/>
          {/* Price */}
          <div className="stock-edit-block">
            <label className="form-label">
            Price
            <input
              type="number"
              name="price"
              className="input-field"
              placeholder="Price ($)"
              required
              value={product.price}
              onChange={handleChange}
              min={0}
            />
            </label>
          </div>

          {/* Stock */}
          <div className="stock-edit-block">
            <label className="form-label">
              Stock Quantity
              <input
                type="number"
                name="stock"
                className="input-field"
                required
                value={product.stock}
                onChange={handleChange}
                min={0}
              />
            </label>
          </div>
          <hr style={{margin: "1rem 0 0.5rem 0"}}/>
          {/* Featured toggle */}
          <label className="form-label checkbox-label">
            <input
              type="checkbox"
              checked={product.featured === 1}
              onChange={(e) =>
                setProduct({
                  ...product,
                  featured: e.target.checked ? 1 : 0,
                })
              }
            />
            Mark as Featured Product? (Featured products appear on the homepage.)
          </label>

          <hr style={{margin: "0.5rem 0 1rem 0"}}/>

          {/* Submit / Cancel */}
          <div className="product-buttons admin-buttons">
            <button type="submit" className="add-to-cart-button">
              <i className="fa-solid fa-circle-check"></i> {isEdit ? "Update Product" : "Create Product"}
            </button>

            <button
              type="button"
              className="buy-now-button"
              onClick={() => navigate("/admin/products")}
            >
              Cancel <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

        </div>
      </div>

    </div>
  </div>
  </form>
  </div>
);

      
};

export default AdminProductForm;
